<script setup lang="ts">
import { computed } from 'vue';
import { DgrCheckbox } from '@stockmarkteam/donguri-ui';
import ClickableQuery from '@/components/theme/edit/theme-setting-support-modal/clickable-query.vue';
import { useThemeSettingSupportModal } from '@/components/theme/edit/theme-setting-support-modal/useThemeSettingSupportModal';
import {
  ExcludedKeyword,
  GeneratedQuery,
  isExcludedKeyword,
  Keyword,
  OpenPopupParam,
  ThemeQueryGenerationRequest,
} from '@/types/themeQueryGenerationRequest';

export interface Props {
  keywordDataset: GeneratedQuery['keywords'];
  excludedKeywords: GeneratedQuery['excluded_keywords'];
  explanation: GeneratedQuery['explanation'];
  deletedKeywords: ThemeQueryGenerationRequest['deleted_keywords'];
  filteredKeywordsJa: Keyword[][];
  filteredKeywordsEn: Keyword[][];
  filteredExcludedKeywordsJa: ExcludedKeyword[];
  filteredExcludedKeywordsEn: ExcludedKeyword[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'open-popup', param: OpenPopupParam): void;
  (e: 'add-queries'): void;
}>();
const showEnglishQuery = defineModel<boolean>({ required: true });

const { createUuidMap } = useThemeSettingSupportModal();

const openPopup = ({ event, word, lang, isDeletedKeyword }: OpenPopupParam) => {
  emit('open-popup', {
    event,
    word,
    lang,
    isDeletedKeyword,
  });
};
const addQueries = () => {
  emit('add-queries');
};

/**
 * 送信ボタンを有効にするかどうか
 *
 * 送信ボタンを有効にする条件:
 * - 日本語のキーワードが1つ以上存在する場合 or 英語のクエリが表示されていて、英語のキーワードが1つ以上存在する場合
 */
const enableSubmitButton = computed(() => {
  return (
    props.filteredKeywordsJa.length > 0 ||
    (showEnglishQuery.value && props.filteredKeywordsEn.length > 0)
  );
});

/**
 * `keywordDataset` と `excludedKeywords` を基に UUID マップを作成する
 *
 * @returns {Object} UUID マップ
 */
const uuidMap = computed(() =>
  createUuidMap({
    keywordDataset: props.keywordDataset,
    excludedKeywords: props.excludedKeywords,
  }),
);

const deletedKeywordsOrderedByLang = computed(() =>
  [...props.deletedKeywords]
    .sort((a, b) => {
      // 日本語 -> 英語の順に並び替える
      if (a.lang === 'ja' && b.lang === 'en') return -1;
      if (a.lang === 'en' && b.lang === 'ja') return 1;
      return 0;
    })
    // "英語クエリを表示"がオフの場合は英語の削除キーワードを表示しない
    .filter(
      deleted_keyword =>
        showEnglishQuery.value || deleted_keyword.lang !== 'en',
    ),
);
</script>

<template>
  <div class="query-section">
    <div class="section">
      <div class="c-title-bold--m">検索クエリ</div>
      <div>
        <div class="c-title-bold--s query-lang">日本語</div>
        <div class="query">
          <ClickableQuery
            @open-popup="openPopup"
            :keyword-dataset="filteredKeywordsJa"
            :excluded-keywords="filteredExcludedKeywordsJa"
            lang="ja"
          />
        </div>
      </div>
      <template v-if="showEnglishQuery">
        <div>
          <div class="c-title-bold--s query-lang">英語</div>
          <div class="query">
            <ClickableQuery
              @open-popup="openPopup"
              :keyword-dataset="filteredKeywordsEn"
              :excluded-keywords="filteredExcludedKeywordsEn"
              lang="en"
            />
          </div>
        </div>
      </template>
      <div class="c-title-bold--s" v-if="deletedKeywords.length > 0">
        削除したキーワード
      </div>
      <div class="c-text-regular--m">
        <div class="deleted-keyword-wrapper">
          <div
            v-for="deletedKeyword in deletedKeywordsOrderedByLang"
            :key="`${deletedKeyword.uuid}-${deletedKeyword.lang}`"
            class="deleted-keyword"
            @click.stop="
              openPopup({
                event: $event,
                word: uuidMap.get(deletedKeyword.uuid) as
                  | Keyword
                  | ExcludedKeyword,
                lang: deletedKeyword.lang,
                isDeletedKeyword: true,
              })
            "
            role="button"
          >
            {{
              (isExcludedKeyword(uuidMap.get(deletedKeyword.uuid)) ? '-' : '') +
              uuidMap.get(deletedKeyword.uuid)?.[deletedKeyword.lang]
            }}
          </div>
        </div>
      </div>
      <div class="controls">
        <DgrCheckbox
          class="o-checkbox-label c-text-regular--m"
          v-model="showEnglishQuery"
          >英語クエリを表示</DgrCheckbox
        >
        <button
          class="c-btn--AnewsPrimary c-btn--small c-btn--auto"
          :class="{
            disabled: !enableSubmitButton,
          }"
          :disabled="!enableSubmitButton"
          @click="addQueries"
        >
          検索クエリを追加
        </button>
      </div>
    </div>
    <div class="section description-section">
      <div class="c-title-bold--m">概要</div>
      <div class="description">
        {{ explanation }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 4px;
  border: 1px solid $color-gray400;
  padding: 16px;
}
.query-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.query-lang {
  margin-bottom: 4px;
}
.query {
  padding: 8px;
  word-break: break-all;
  border-radius: 4px;
  border: 1px solid $color-gray400;
}
.deleted-keyword-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.deleted-keyword {
  border: 1px solid $color-gray400;
  border-radius: 100px;
  padding: 8px 10px;
  display: inline-block;
  cursor: pointer;
}
.controls {
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}
.popup-trigger {
  cursor: pointer;
  &:hover {
    background: $color-gray400;
    border-radius: 4px;
  }
}
.description-section {
  margin-bottom: 16px;
}
.description {
  padding: 8px;
  white-space: pre-wrap;
}
</style>
